import { LocationType } from 'api-client'
import {
  ErrorEnum,
  OrderLineStatus,
  OrderRequest,
  OrderResponse,
  StatusEnum,
} from 'api-order-client'
import { equals, sortBy } from 'ramda'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { redirect } from 'redux-first-router'
import { v4 as uuidv4 } from 'uuid'

import {
  placeOrder,
  validateCart as validateCartApi,
} from '~shared-components/api/orders'
import { RouterLink } from '~shared-components/components/RouterLink/RouterLink'
import { useLoggedInUser } from '~shared-components/hooks/useLoggedInUser'
import { useToaster } from '~shared-components/hooks/useToaster'
import { useTranslations } from '~shared-components/hooks/useTranslations'
import {
  BaseCartProduct,
  Cart,
  CartCustomerContext,
  OrderDetailsData,
  ProductDetailData,
} from '~shared-components/model'
import {
  billToAddressChangeAction,
  initCartAction,
  initCartValidationAction,
  orderFailure,
  orderSuccess,
  populateCartContentsAction,
  removeFromCartAction,
  shipToAddressChangeAction,
  totalValueChangeAction,
  updateCartStatus,
  updateCartWithValidation,
  updateProductQuantityAction,
} from '~shared-components/redux/actions/cart.actions'
import {
  loadCartState,
  saveCartState,
  SavedCart,
} from '~shared-components/redux/localStorage'
import { CartAddressChange } from '~shared-components/redux/reducers/cart.reducers'
import { CartSelector } from '~shared-components/redux/selectors/cart.selector'
import { ConfigSelector } from '~shared-components/redux/selectors/config.selector'
import { LocationSelector } from '~shared-components/redux/selectors/location.selector'
import { LoginSelector } from '~shared-components/redux/selectors/login.selector'
import { Currency, Price } from '~shared-components/transforms'
import {
  OrderRequestWithOptionalMeta,
  transformCartToOrder,
} from '~shared-components/transforms/cart.transforms'
import {
  ApplicationUser,
  CustomerContext,
  CustomerLocation,
} from '~shared-components/transforms/user.transforms'
import { wrapStringWithElement } from '~shared-components/translations/default'
import { isServer } from '~shared-components/utils'
import { Page } from '~shared-components/utils/enums'
import { locationToString } from '~shared-components/utils/misc'
import { canSumPrices, sumPrices } from '~shared-components/utils/price-utils'

const findMatchingPermission = (
  customerContext: CartCustomerContext,
  customers: CustomerContext[] = [],
) =>
  customers.find(
    ({ customerContextId, customerNumber }) =>
      customerNumber === customerContext.customerNumber &&
      customerContextId === customerContext.customerContextId,
  )

const getCartLocations = (user: ApplicationUser, cart: SavedCart | Cart) => {
  const match = findMatchingPermission(cart.customerContext, user?.customers)

  const locations = sortBy<CustomerLocation>(
    item => item.name,
    match?.locations ?? [],
  )

  const shipToLocation = cart.shipToLocation
    ? locations.find(l => l.id === cart.shipToLocation)
    : undefined

  const billToLocation = cart.billToLocation
    ? locations.find(l => l.id === cart.billToLocation)
    : undefined

  const firstBillTo = locations.find(l => l.type === LocationType.BillTo)
  const firstShipTo = locations.find(l => l.type === LocationType.ShipTo)

  return {
    billToLocation: billToLocation ?? firstBillTo,
    shipToLocation: shipToLocation ?? firstShipTo,
  }
}

export const useCart = () => {
  const t = useTranslations()
  const dispatch = useDispatch()
  const { user } = useLoggedInUser()
  const { showErrorToast, showSuccessToast } = useToaster()
  const [needToSave, setNeedToSave] = useState<number>(0)

  const apiBaseUrl = useSelector(ConfigSelector.apiBaseUrl)
  const cart: Cart | undefined = useSelector(CartSelector.cart)
  const cartCurrency = useSelector(CartSelector.currency)
  const cartLocation = useSelector(CartSelector.shipToLocation)
  const cartState = useSelector(CartSelector.cartState)
  const payload = useSelector(LocationSelector.payload, equals)
  const populated = cartState.populated
  const customerContext = useSelector(LoginSelector.selectedCustomerContext)
  const selectedLocation = useSelector(LoginSelector.selectedLocation)
  const selectedLocationId = useSelector(LoginSelector.selectedLocationId)

  useEffect(() => {
    if (populated || !user) return

    dispatch(initCartAction())

    if (isServer()) return

    const loadedCartState = loadCartState(user.email)

    const newCartState = {
      ...loadedCartState,
      carts: loadedCartState?.carts?.map(cart => {
        const { billToLocation, shipToLocation } = getCartLocations(user, cart)
        const products = cart.products.map(p => ({
          ...p,
          price: p.price
            ? new Price({
                ...p.price,
                currency: p.price.currency as Currency,
              })
            : undefined,
        }))
        const prices = products.map(({ price }) => price)
        const totalValue = canSumPrices(prices) ? sumPrices(prices) : undefined

        return {
          ...cart,
          billToLocation,
          isValid: cart.isValid && totalValue !== undefined,
          products,
          shipToLocation,
          totalValue,
        }
      }),
    }
    dispatch(populateCartContentsAction(newCartState))
  }, [dispatch, populated, user])

  useEffect(() => {
    if (!user || !cart) return
    const { billToLocation, shipToLocation } = getCartLocations(user, cart)

    if (cart.billToLocation === undefined) {
      dispatch(
        billToAddressChangeAction({
          customerContext: cart.customerContext,
          location: billToLocation,
        }),
      )
    }

    if (cart.shipToLocation === undefined) {
      dispatch(
        shipToAddressChangeAction({
          customerContext: cart.customerContext,
          location: shipToLocation,
        }),
      )
    }
  }, [cart, dispatch, user])
  // Set default bill to location and/or shipto location

  useEffect(() => {
    if (!needToSave) return
    saveCartState(user!.email, cartState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needToSave])

  return useMemo(() => {
    const save = () => setNeedToSave(state => ++state % 1234567)
    const isCartLocked = (currency: string | undefined) => {
      const locationChanged =
        cartLocation && selectedLocationId !== cartLocation.id
      const currencyChanged =
        cartCurrency !== undefined && cartCurrency !== currency

      if (locationChanged) {
        showErrorToast(
          wrapStringWithElement(
            t('cart.addtocart.shiptolocked').replace(
              '@@LOCATION@@',
              locationToString(cartLocation),
            ),
            '@@SHOPPING_CART@@',
            t('label.shoppingcart').toLowerCase(),
            React.createElement(RouterLink, { to: { type: Page.Cart } }),
          ),
          9000,
        )
      }

      if (currencyChanged) {
        showErrorToast(
          wrapStringWithElement(
            t('cart.addtocart.currencylocked').replace(
              '@@CURRENCY@@',
              cartCurrency!,
            ),
            '@@SHOPPING_CART@@',
            t('label.shoppingcart').toLowerCase(),
            React.createElement(RouterLink, { to: { type: Page.Cart } }),
          ),
          9000,
        )
      }

      return locationChanged || currencyChanged
    }

    const updateProductQuantity = (product: BaseCartProduct) => {
      if (!customerContext) throw new Error('Customer context is missing')

      const c: Cart = cart || {
        customerContext,
        products: [],
      }

      dispatch(
        updateProductQuantityAction({ location: selectedLocation, product }),
      )

      const products = [
        ...c.products.filter(p => p.data.id !== product.data.id),
        product,
      ]
      const prices = products.map(({ price }) => price)
      const totalValue = canSumPrices(prices) ? sumPrices(prices) : undefined

      dispatch(
        totalValueChangeAction({
          customerContext: product.customerContext,
          totalValue,
        }),
      )

      const isUpdatingExistingProduct = Boolean(
        c.products.some(p => p.data.id === product.data.id),
      )
      if (product.quantity === 0) {
        showSuccessToast(
          t('cart.removefromcart.success').replace(
            '@@PRODUCT@@',
            product.data.name,
          ),
        )
      } else if (isUpdatingExistingProduct) {
        showSuccessToast(
          t('cart.updatecart.success').replace(
            '@@PRODUCT@@',
            product.data.name,
          ),
        )
      } else {
        showSuccessToast(
          t('cart.addtocart.success').replace('@@PRODUCT@@', product.data.name),
        )
      }

      save()
    }

    const checkHasProductsInCart = (product: BaseCartProduct): boolean => {
      if (!customerContext) throw new Error('Customer context is missing')

      const c: Cart = cart || {
        customerContext,
        products: [],
      }
      const hasProductsInCart = Boolean(c.products.length)

      if (!hasProductsInCart) {
        showSuccessToast(
          t('cart.addtocart.locked')
            .replace('@@CURRENCY@@', product.price?.getCurrency() || '')
            .replace('@@LOCATION@@', locationToString(selectedLocation)),
        )
      }

      return hasProductsInCart
    }

    const checkCanOrderProduct = (product: BaseCartProduct) =>
      product.data.name !== undefined &&
      !isCartLocked(product.price?.getCurrency())

    const findProductInCart = (productId: string, quantity = 0) =>
      cart?.products.find(
        cartProduct =>
          cartProduct.data.id === productId &&
          (cartProduct.quantity || 0) >= quantity,
      )

    const checkProductInCart = (product: BaseCartProduct) => {
      const isExistingProduct = Boolean(
        findProductInCart(product.data.id, product.quantity),
      )
      if (isExistingProduct) {
        showSuccessToast(
          t('cart.addtocart.alreadyincart').replace(
            '@@PRODUCT@@',
            product.data.name,
          ),
        )
      }
      return isExistingProduct
    }

    const orderProduct = (
      product: BaseCartProduct,
      updateExistingProduct?: boolean,
    ) => {
      checkHasProductsInCart(product)

      if (updateExistingProduct || !checkProductInCart(product)) {
        updateProductQuantity(product)
      }
    }

    const reorderProduct = (orderData: OrderDetailsData['reorderData']) => {
      if (!customerContext) throw new Error('Customer context is missing')

      const product: BaseCartProduct = {
        ...orderData,
        customerContext,
      }
      checkCanOrderProduct(product) && orderProduct(product)
    }

    const reorderOrders = (orders: OrderDetailsData[]) => {
      if (!customerContext) throw new Error('Customer context is missing')

      let cartLockedChecked = false
      orders
        .map(order => ({
          ...order,
          product: {
            ...order.reorderData,
            customerContext,
          },
        }))
        .filter(
          ({ canReorder, orderStatus, product }) =>
            canReorder &&
            orderStatus !== OrderLineStatus.Cancelled &&
            product &&
            checkCanOrderProduct(product),
        )
        .forEach(({ product }) => {
          if (!cartLockedChecked && !checkHasProductsInCart(product)) {
            cartLockedChecked = true
          }
          if (!checkProductInCart(product)) {
            updateProductQuantity(product)
          }
        })
    }

    const handleSubmitCartSuccess = (
      context: CustomerContext,
      orderId: string,
      user: ApplicationUser | undefined,
      // _response?: OrderResponse,
    ) => {
      dispatch(
        orderSuccess(
          {
            customerName: context.customerName || '',
            customerNumber: context.customerNumber || '',
            orderCreationDate: Date.now(),
            orderNumber: orderId,
          },
          user!.email,
        ),
      )

      dispatch(
        redirect({
          payload: {
            id: orderId,
            ...payload,
          },
          type: Page.OrderConfirmation,
        }),
      )
    }

    const handleSubmitError = () => {
      dispatch(orderFailure())
      showErrorToast(t('toast.error.placingorder'))
    }

    const handleSubmitValidationError = (
      context: CustomerContext,
      response: OrderResponse,
    ) => {
      if (cart) {
        dispatch(
          updateCartWithValidation(
            {
              currentCart: cart,
              customerContextId: context.customerContextId,
              validationResponse: response,
            },
            user!.email,
          ),
        )

        if (
          response.data.relationships.customerContext.meta?.error ===
          ErrorEnum.NotFound
        ) {
          showErrorToast(t('toast.error.customernotfound'))
        }
      } else {
        // No cart found
        handleSubmitError()
      }
    }

    const validateCart = async (): Promise<
      OrderRequestWithOptionalMeta | undefined
    > => {
      dispatch(initCartValidationAction())

      if (!customerContext) return
      const { customerContextId } = customerContext
      // Each customer context can only have one sold-to location
      const soldTo = customerContext.locations.find(
        (l: CustomerLocation) => l.type === LocationType.SoldTo,
      )
      let order: OrderRequestWithOptionalMeta | undefined

      if (cart && soldTo) {
        order = transformCartToOrder(cart, soldTo.id)
        try {
          const validationResponse = await validateCartApi(apiBaseUrl, order)
          dispatch(
            updateCartWithValidation(
              {
                currentCart: cart,
                customerContextId,
                validationResponse,
              },
              user!.email,
            ),
          )
        } catch (e) {
          showErrorToast(t('toast.error.validatingcart'))
        }

        if (cart && cartLocation) {
          const prices = cart.products.map(({ price }) => price)
          const totalValue = canSumPrices(prices)
            ? sumPrices(prices)
            : undefined

          if (totalValue === undefined) {
            showErrorToast(
              wrapStringWithElement(
                t('cart.addtocart.currencylocked').replace(
                  '@@CURRENCY@@',
                  cartCurrency!,
                ),
                '@@SHOPPING_CART@@',
                t('label.shoppingcart').toLowerCase(),
                React.createElement(RouterLink, { to: { type: Page.Cart } }),
              ),
              9000,
            )
          }
        }
      }
      return order
    }

    const submitCart = async () => {
      if (!customerContext) return
      dispatch(updateCartStatus({ loading: true }))

      const order:
        | OrderRequestWithOptionalMeta
        | undefined = await validateCart()

      if (!cart || !order || !cart.deliveryDate || !cart.platformId) {
        return
      }

      // This ID is just so that we can show the order confirmation page.
      // We don't get the real order ID until it has been digested by SAP
      const orderId = uuidv4()

      try {
        const result = await placeOrder(apiBaseUrl, order as OrderRequest)

        if (
          result?.status === 200 &&
          result?.data.meta.status === StatusEnum.Ok
        ) {
          handleSubmitCartSuccess(customerContext, orderId, user)
        } else if (result.data.meta.status === StatusEnum.ValidationError) {
          handleSubmitValidationError(customerContext, result?.data)
        } else {
          // Kafka error
          handleSubmitError()
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e)
        handleSubmitError()
      }
    }

    return {
      changeBillToAddress(addressChange: CartAddressChange) {
        dispatch(billToAddressChangeAction(addressChange))
        save()
      },
      changeShipToAddress(addressChange: CartAddressChange) {
        dispatch(shipToAddressChangeAction(addressChange))
        save()
      },
      findProductInCart,
      isCartLocked,
      orderProduct,
      removeFromCart(
        productId: ProductDetailData['id'],
        customerContext: Cart['customerContext'],
      ) {
        dispatch(
          removeFromCartAction({ customerContext, productId }, user!.email),
        )
        save()
      },
      reorderOrders,
      reorderProduct,
      saveCart: save,
      submitCart,
      validateCart,
    }
  }, [
    apiBaseUrl,
    cart,
    cartCurrency,
    cartLocation,
    dispatch,
    payload,
    customerContext,
    selectedLocation,
    selectedLocationId,
    showErrorToast,
    showSuccessToast,
    t,
    user,
  ])
}
